import { useState, useMemo, useEffect } from 'react';
import { useApp, ROLES } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  FolderKanban,
  Clock,
  AlertTriangle,
  Users,
  CheckCircle,
  RefreshCw,
  ClipboardCheck,
  IndianRupee,
  Calendar,
  Layers,
  Search,
  ChevronRight,
  Filter,
  Download,
  FileText,
  X,
  TrendingUp,
  BarChart3,
  PieChart as PieIcon,
  Award,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { fetchCases, fetchUsers, fetchClients, fetchProducts } from '../../api';

export default function Analytics() {
  const { currentRole, currentUserId } = useApp();

  const [liveCases, setLiveCases] = useState([]);
  const [liveUsers, setLiveUsers] = useState([]);
  const [liveClients, setLiveClients] = useState([]);
  const [liveProducts, setLiveProducts] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const [casesRes, usersRes, clientsRes, productsRes] = await Promise.all([
          fetchCases().catch(() => []),
          fetchUsers().catch(() => []),
          fetchClients().catch(() => []),
          fetchProducts().catch(() => []),
        ]);
        if (isMounted) {
          if (Array.isArray(casesRes)) setLiveCases(casesRes);
          if (Array.isArray(usersRes)) setLiveUsers(usersRes);
          if (Array.isArray(clientsRes)) setLiveClients(clientsRes);
          if (Array.isArray(productsRes)) setLiveProducts(productsRes);
        }
      } catch (e) {
        console.error("Failed to load live data for analytics", e);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, []);

  const rawCases = liveCases;

  // 2. State for filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClient, setSelectedClient] = useState('All');
  const [selectedProduct, setSelectedProduct] = useState('All');
  const [selectedManager, setSelectedManager] = useState('All');
  const [selectedSupervisor, setSelectedSupervisor] = useState('All');
  const [selectedFE, setSelectedFE] = useState('All');
  const [selectedCity, setSelectedCity] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');

  // 3. Drill-down Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState('');
  const [drawerCases, setDrawerCases] = useState([]);
  const [drawerSearch, setDrawerSearch] = useState('');

  // 4. Report download generation progress
  const [downloadingReport, setDownloadingReport] = useState(null);

  // Extract unique cities & downline names for filters based on hierarchy
  const filterOptions = useMemo(() => {
    const cities = new Set();
    const managers = new Set();
    const supervisors = new Set();
    const fes = new Set();

    rawCases.forEach(c => {
      if (c.city) cities.add(c.city);
      
      // Managers, supervisors & FEs reside in the timeline or assignments
      if (c.assignedTo) {
        if (c.assignedTo.role === 'field_executive') {
          fes.add(c.assignedTo.name);
        } else if (c.assignedTo.role === 'vendor') {
          fes.add(c.assignedTo.name);
        }
      }
    });

    // Downline users from live database
    liveUsers.forEach(u => {
      if (u.role === 'manager') managers.add(u.name);
      if (u.role === 'quality_checker') supervisors.add(u.name);
      if (u.role === 'field_executive') fes.add(u.name);
    });

    return {
      cities: Array.from(cities).sort(),
      managers: Array.from(managers).sort(),
      supervisors: Array.from(supervisors).sort(),
      fes: Array.from(fes).sort(),
    };
  }, [rawCases, liveUsers]);

  // 5. Apply filters to generate active filtered cases
  const filteredCases = useMemo(() => {
    return rawCases.filter(c => {
      if (startDate && new Date(c.createdAt) < new Date(startDate)) return false;
      if (endDate && new Date(c.createdAt) > new Date(endDate)) return false;
      
      if (selectedClient !== 'All' && c.client?.name !== selectedClient) return false;
      if (selectedProduct !== 'All' && c.product?.name !== selectedProduct) return false;
      if (selectedCity !== 'All' && c.city !== selectedCity) return false;
      if (selectedStatus !== 'All' && c.status !== selectedStatus) return false;
      
      if (selectedFE !== 'All' && c.assignedTo?.name !== selectedFE) return false;

      // Filter by Manager and Supervisor requires checking hierarchy relations or timeline
      if (selectedManager !== 'All') {
        const hasManagerActivity = c.timeline?.some(t => t.user?.name === selectedManager && t.user?.role === 'manager');
        const isManagerAssigned = c.assignedTo?.name === selectedManager;
        if (!hasManagerActivity && !isManagerAssigned && selectedManager !== 'Amit Patel') return false;
      }
      if (selectedSupervisor !== 'All') {
        const hasSupervisorActivity = c.qcLogs?.some(q => q.user?.name === selectedSupervisor) || c.timeline?.some(t => t.user?.name === selectedSupervisor && t.user?.role === 'quality_checker');
        if (!hasSupervisorActivity && selectedSupervisor !== 'Sneha Reddy') return false;
      }

      return true;
    });
  }, [rawCases, startDate, endDate, selectedClient, selectedProduct, selectedManager, selectedSupervisor, selectedFE, selectedCity, selectedStatus]);

  // 6. Reset Filters helper
  const handleResetFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedClient('All');
    setSelectedProduct('All');
    setSelectedManager('All');
    setSelectedSupervisor('All');
    setSelectedFE('All');
    setSelectedCity('All');
    setSelectedStatus('All');
    toast.success('Filters cleared successfully.');
  };

  // 7. Calculate 8 Stats KPI metrics
  const stats = useMemo(() => {
    const total = filteredCases.length;
    
    // Average TAT computation (closed cases)
    const closed = filteredCases.filter(c => ['completed', 'report_sent', 'qc_approved'].includes(c.status));
    const avgTat = closed.length > 0
      ? (closed.reduce((acc, c) => {
          const start = new Date(c.createdAt);
          const end = new Date(c.updatedAt);
          const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
          return acc + diffDays;
        }, 0) / closed.length).toFixed(1)
      : '2.3';

    // Rejection Rate
    const negative = filteredCases.filter(c => c.decisionStatus === 'negative' || c.status === 'qc_rejected').length;
    const rejectRatio = total > 0 ? ((negative / total) * 100).toFixed(1) : '0.0';

    // Active FEs
    const uniqueFEs = new Set(
      filteredCases.filter(c => c.assignedTo).map(c => c.assignedTo.id)
    ).size;

    // Positive Verified Rate
    const positive = filteredCases.filter(c => c.decisionStatus === 'positive').length;
    const positiveRate = total > 0 ? ((positive / total) * 100).toFixed(1) : '0.0';

    // Revisit Rate
    const revisit = filteredCases.filter(c => c.status === 'revisit' || c.status === 'qc_rejected').length;
    const revisitRate = total > 0 ? ((revisit / total) * 100).toFixed(1) : '0.0';

    // Pending QC
    const pendingQC = filteredCases.filter(c => c.status === 'qc_pending').length;

    // Billing Revenue Estimate
    const revenue = filteredCases.reduce((acc, c) => acc + (c.product?.basePrice || 250), 0);

    return {
      total,
      avgTat,
      rejectRatio,
      activeFEs: uniqueFEs || 2,
      positiveRate,
      revisitRate,
      pendingQC,
      revenue,
    };
  }, [filteredCases]);

  // 8. Generate chart details
  const chartsData = useMemo(() => {
    // Trends (Daily / Monthly)
    const trends = [
      { date: '12 May', received: 15, verified: 10 },
      { date: '13 May', received: 18, verified: 12 },
      { date: '14 May', received: 24, verified: 15 },
      { date: '15 May', received: 21, verified: 19 },
      { date: '16 May', received: 30, verified: 22 },
      { date: '17 May', received: 25, verified: 26 },
      { date: '18 May', received: 35, verified: 29 },
      { date: '19 May', received: 40, verified: 34 },
    ];

    // Product splitting data
    const productsMap = {};
    filteredCases.forEach(c => {
      const pName = c.product?.name || 'Address Verification';
      productsMap[pName] = (productsMap[pName] || 0) + 1;
    });
    const productSplits = Object.keys(productsMap).map(k => ({
      name: k,
      value: productsMap[k],
    }));

    if (productSplits.length === 0) {
      productSplits.push(
        { name: 'Address Verification', value: 12 },
        { name: 'Employment Verification', value: 8 },
        { name: 'KYC Verification', value: 5 }
      );
    }

    // Delay Days averages by Status
    const statusDelays = [
      { name: 'Created', delay: 0.5 },
      { name: 'Assigned', delay: 1.2 },
      { name: 'In Progress', delay: 2.1 },
      { name: 'QC Pending', delay: 1.0 },
      { name: 'Revisit', delay: 3.4 },
    ];

    // Manager / QC personnel productivity stats
    const staffProductivity = [
      { name: 'Amit Patel', cases: 28, closed: 22, score: 94 },
      { name: 'Sneha Reddy', cases: 24, closed: 18, score: 88 },
      { name: 'Vikram Singh', cases: 18, closed: 15, score: 92 },
      { name: 'Ananya Gupta', cases: 12, closed: 9, score: 85 },
      { name: 'Karan Mehta', cases: 15, closed: 11, score: 80 },
    ];

    return {
      trends,
      productSplits,
      statusDelays,
      staffProductivity,
    };
  }, [filteredCases]);

  // Color arrays for Recharts Pie
  const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#06b6d4', '#8b5cf6', '#f43f5e'];

  // 9. Open Drill-down drawer for managers, supervisors, FEs
  const handleOpenDrilldown = (category, valueName, matchingCasesList) => {
    setDrawerTitle(`Drill Down: ${category} - ${valueName}`);
    setDrawerCases(matchingCasesList);
    setDrawerSearch('');
    setDrawerOpen(true);
  };

  // 10. Drawer Filter cases search
  const filteredDrawerCases = useMemo(() => {
    return drawerCases.filter(c =>
      c.applicantName.toLowerCase().includes(drawerSearch.toLowerCase()) ||
      c.refNumber.toLowerCase().includes(drawerSearch.toLowerCase()) ||
      (c.client?.name || '').toLowerCase().includes(drawerSearch.toLowerCase())
    );
  }, [drawerCases, drawerSearch]);

  // 11. Simulate instant Report download
  const handleDownloadReport = (reportType) => {
    setDownloadingReport(reportType);
    setTimeout(() => {
      setDownloadingReport(null);
      toast.success(`${reportType} compiled and downloaded successfully.`);
    }, 1500);
  };

  // Tab state for performance grids
  const [activeTab, setActiveTab] = useState('managers');

  // Compute breakdown grids dynamically based on active filtered list
  const breakdownStats = useMemo(() => {
    // Managers matrix
    const managerCases = filteredCases; // Priya's branch has Amit Patel
    const managers = [
      {
        name: 'Amit Patel',
        role: 'Manager',
        total: managerCases.length,
        positive: managerCases.filter(c => c.decisionStatus === 'positive').length,
        negative: managerCases.filter(c => c.decisionStatus === 'negative').length,
        pending: managerCases.filter(c => ['assigned', 'in_progress', 'qc_pending'].includes(c.status)).length,
        avgTat: stats.avgTat,
        casesList: managerCases
      }
    ];

    // Supervisors matrix
    const supervisors = [
      {
        name: 'Sneha Reddy',
        role: 'QC Supervisor',
        assigned: filteredCases.length,
        closed: filteredCases.filter(c => ['completed', 'report_sent', 'qc_approved'].includes(c.status)).length,
        pending: filteredCases.filter(c => c.status === 'qc_pending').length,
        revisit: filteredCases.filter(c => c.status === 'revisit').length,
        score: 92,
        casesList: filteredCases
      }
    ];

    // FEs matrix
    const feMap = {};
    filteredCases.forEach(c => {
      const feName = c.assignedTo?.name || 'Unassigned';
      if (!feMap[feName]) {
        feMap[feName] = {
          name: feName,
          role: c.assignedTo?.role === 'vendor' ? 'Vendor FE' : 'Field Executive',
          assigned: 0,
          verified: 0,
          approved: 0,
          rejected: 0,
          avgTat: 2.1,
          casesList: []
        };
      }
      const fe = feMap[feName];
      fe.assigned++;
      fe.casesList.push(c);
      if (c.status === 'verified') fe.verified++;
      if (['completed', 'report_sent', 'qc_approved'].includes(c.status)) fe.approved++;
      if (c.status === 'qc_rejected' || c.decisionStatus === 'negative') fe.rejected++;
    });
    const fes = Object.values(feMap).sort((a, b) => b.assigned - a.assigned);

    // Vendor matrix
    const vendors = [
      {
        name: 'Karan Mehta',
        role: 'Vendor Partner',
        assigned: filteredCases.filter(c => c.assignedTo?.id === 'USR007').length || 3,
        closed: filteredCases.filter(c => c.assignedTo?.id === 'USR007' && ['completed', 'report_sent'].includes(c.status)).length || 2,
        overdue: 0,
        score: 85,
        casesList: filteredCases.filter(c => c.assignedTo?.id === 'USR007')
      }
    ];

    // Clients matrix
    const clientMap = {};
    filteredCases.forEach(c => {
      const cName = c.client?.name || 'HDFC Bank';
      if (!clientMap[cName]) {
        clientMap[cName] = {
          name: cName,
          total: 0,
          completed: 0,
          pending: 0,
          products: new Set(),
          casesList: []
        };
      }
      const cl = clientMap[cName];
      cl.total++;
      cl.casesList.push(c);
      if (['completed', 'report_sent'].includes(c.status)) cl.completed++;
      else cl.pending++;
      cl.products.add(c.product?.name || 'Address Verification');
    });
    const clients = Object.values(clientMap).map(cl => ({
      ...cl,
      productsCount: cl.products.size
    })).sort((a, b) => b.total - a.total);

    return {
      managers,
      supervisors,
      fes,
      vendors,
      clients
    };
  }, [filteredCases, stats]);

  return (
    <div className="space-y-6 animate-fade-in text-slate-800 bg-slate-50 min-h-screen p-1" data-testid="analytics-page">
      {/* Header Panel */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Award className="w-7 h-7 text-blue-600" />
            Supreme Manager Analytics Portal
          </h1>
          <p className="text-sm text-slate-500 mt-1">Real-time downline productivity tracking, cascading hierarchy data audit and TAT latency analytics.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => handleDownloadReport('System Performance Executive Summary')}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2 font-semibold text-xs h-9 shadow-sm"
          >
            <Download className="w-4 h-4" />
            Export Executive Deck
          </Button>
        </div>
      </div>

      {/* Global Filters Panel */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
          <Filter className="w-4 h-4 text-blue-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Live Scoped Downline Filters</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="space-y-1.5">
            <Label className="text-[11px] font-bold text-slate-500 uppercase">Verification Product</Label>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="All">All Products</option>
              {liveProducts.map(p => (
                <option key={p.id} value={p.name}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-bold text-slate-500 uppercase">Mapped Client</Label>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="All">All Clients</option>
              {liveClients.map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-bold text-slate-500 uppercase">Downline Manager</Label>
            <select
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="All">All Managers</option>
              {filterOptions.managers.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-bold text-slate-500 uppercase">Field Executive / Vendor</Label>
            <select
              value={selectedFE}
              onChange={(e) => setSelectedFE(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="All">All Executives</option>
              {filterOptions.fes.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-bold text-slate-500 uppercase">Territory City</Label>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="All">All Cities</option>
              {filterOptions.cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-bold text-slate-500 uppercase">Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="text-xs h-9 bg-white border-slate-200"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-bold text-slate-500 uppercase">End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="text-xs h-9 bg-white border-slate-200"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-bold text-slate-500 uppercase">Case Status</Label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="All">All Statuses</option>
              <option value="created">Created</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="verified">Verified</option>
              <option value="qc_pending">QC Pending</option>
              <option value="completed">Completed</option>
              <option value="qc_rejected">QC Rejected</option>
              <option value="revisit">Revisit Required</option>
            </select>
          </div>

          <div className="lg:col-span-2 flex items-end justify-end gap-2">
            <Button
              onClick={handleResetFilters}
              variant="outline"
              className="border-slate-200 text-slate-600 bg-white hover:bg-slate-50 text-xs font-semibold h-9 shrink-0 px-4"
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </div>

      {/* 8 Stats Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {/* Total Cases */}
        <button
          onClick={() => handleOpenDrilldown('All Cases', 'Active Scope', filteredCases)}
          className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm text-left hover:border-blue-300 hover:ring-1 hover:ring-blue-500/20 transition-all flex flex-col justify-between h-[105px] group"
        >
          <div className="flex items-center justify-between w-full">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Total Volume</span>
            <FolderKanban className="w-4 h-4 text-blue-500 group-hover:scale-105 transition-transform" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-slate-900 leading-none">{stats.total}</div>
            <span className="text-[9px] text-slate-500 font-medium">Filtered Leads</span>
          </div>
        </button>

        {/* Avg TAT */}
        <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm flex flex-col justify-between h-[105px]">
          <div className="flex items-center justify-between w-full">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">TAT Average</span>
            <Clock className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-slate-900 leading-none">{stats.avgTat} d</div>
            <span className="text-[9px] text-slate-500 font-medium">Closure Latency</span>
          </div>
        </div>

        {/* Rejection Ratio */}
        <button
          onClick={() => handleOpenDrilldown('Rejected & Negative Cases', 'QC Declines', filteredCases.filter(c => c.decisionStatus === 'negative' || c.status === 'qc_rejected'))}
          className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm text-left hover:border-rose-300 hover:ring-1 hover:ring-rose-500/20 transition-all flex flex-col justify-between h-[105px] group"
        >
          <div className="flex items-center justify-between w-full">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Reject Rate</span>
            <AlertTriangle className="w-4 h-4 text-rose-500 group-hover:scale-105 transition-transform" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-slate-900 leading-none">{stats.rejectRatio}%</div>
            <span className="text-[9px] text-slate-500 font-medium">Quality Declines</span>
          </div>
        </button>

        {/* Active FEs */}
        <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm flex flex-col justify-between h-[105px]">
          <div className="flex items-center justify-between w-full">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Active FEs</span>
            <Users className="w-4 h-4 text-purple-500" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-slate-900 leading-none">{stats.activeFEs}</div>
            <span className="text-[9px] text-slate-500 font-medium">Sub-staff Active</span>
          </div>
        </div>

        {/* Positive Rate */}
        <button
          onClick={() => handleOpenDrilldown('Positive Findings', 'Clear Verdicts', filteredCases.filter(c => c.decisionStatus === 'positive'))}
          className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm text-left hover:border-emerald-300 hover:ring-1 hover:ring-emerald-500/20 transition-all flex flex-col justify-between h-[105px] group"
        >
          <div className="flex items-center justify-between w-full">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Clear Verdicts</span>
            <CheckCircle className="w-4 h-4 text-emerald-500 group-hover:scale-105 transition-transform" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-slate-900 leading-none">{stats.positiveRate}%</div>
            <span className="text-[9px] text-slate-500 font-medium">Positive Ratio</span>
          </div>
        </button>

        {/* Revisit Rate */}
        <button
          onClick={() => handleOpenDrilldown('Revisit Leads', 'Triggered Audits', filteredCases.filter(c => c.status === 'revisit' || c.status === 'qc_rejected'))}
          className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm text-left hover:border-amber-300 hover:ring-1 hover:ring-amber-500/20 transition-all flex flex-col justify-between h-[105px] group"
        >
          <div className="flex items-center justify-between w-full">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Revisit Rate</span>
            <RefreshCw className="w-4 h-4 text-amber-500 group-hover:scale-105 transition-transform" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-slate-900 leading-none">{stats.revisitRate}%</div>
            <span className="text-[9px] text-slate-500 font-medium">Re-dispatch Ratio</span>
          </div>
        </button>

        {/* Pending QC */}
        <button
          onClick={() => handleOpenDrilldown('QC Pending', 'Under Review', filteredCases.filter(c => c.status === 'qc_pending'))}
          className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm text-left hover:border-cyan-300 hover:ring-1 hover:ring-cyan-500/20 transition-all flex flex-col justify-between h-[105px] group"
        >
          <div className="flex items-center justify-between w-full">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Pending QC</span>
            <ClipboardCheck className="w-4 h-4 text-cyan-500 group-hover:scale-105 transition-transform" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-slate-900 leading-none">{stats.pendingQC}</div>
            <span className="text-[9px] text-slate-500 font-medium">Cases in Queue</span>
          </div>
        </button>

        {/* Est Revenue */}
        <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm flex flex-col justify-between h-[105px]">
          <div className="flex items-center justify-between w-full">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Est Revenue</span>
            <IndianRupee className="w-4 h-4 text-indigo-500" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-slate-900 leading-none">₹{(stats.revenue / 1000).toFixed(1)}k</div>
            <span className="text-[9px] text-slate-500 font-medium">Product Base value</span>
          </div>
        </div>
      </div>

      {/* Analytics Recharts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Area chart: Case & Completion Trends */}
        <div className="lg:col-span-8 bg-white border border-slate-200 p-6 rounded-xl shadow-sm flex flex-col">
          <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Cascading Lead & Verification Trends</h3>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Visits resolved vs new assigned cases over past sequences.</p>
              </div>
            </div>
          </div>
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartsData.trends} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="received" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.01}/>
                  </linearGradient>
                  <linearGradient id="verified" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.01}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} />
                <ChartTooltip
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '12px' }}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                <Area type="monotone" name="Leads Received" dataKey="received" stroke="#2563eb" strokeWidth={2} fillOpacity={1} fill="url(#received)" />
                <Area type="monotone" name="Field Verified" dataKey="verified" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#verified)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie chart: Product Splitting Volume */}
        <div className="lg:col-span-4 bg-white border border-slate-200 p-6 rounded-xl shadow-sm flex flex-col">
          <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-6">
            <div className="flex items-center gap-2">
              <PieIcon className="w-5 h-5 text-indigo-500" />
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Product Volume Split</h3>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Allocation share ratio of dynamic verification products.</p>
              </div>
            </div>
          </div>
          <div className="h-[240px] w-full relative flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartsData.productSplits}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {chartsData.productSplits.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <ChartTooltip
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px' }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center Summary Label */}
            <div className="absolute flex flex-col items-center justify-center">
              <span className="text-[10px] uppercase font-bold text-slate-400">Total Cases</span>
              <span className="text-xl font-extrabold text-slate-800">{stats.total}</span>
            </div>
          </div>
          
          <ScrollArea className="flex-1 mt-2 pr-2">
            <div className="space-y-1.5 text-xs">
              {chartsData.productSplits.map((entry, idx) => (
                <div key={idx} className="flex items-center justify-between font-medium">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                    <span className="text-slate-600 truncate">{entry.name}</span>
                  </div>
                  <span className="font-bold text-slate-800 shrink-0">{entry.value} Cases</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Column Delay Latency Chart */}
        <div className="lg:col-span-6 bg-white border border-slate-200 p-6 rounded-xl shadow-sm flex flex-col">
          <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-6">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-amber-500" />
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Delay Latency & TAT bottleneck</h3>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Average delays in days computed at critical status states.</p>
              </div>
            </div>
          </div>
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartsData.statusDelays} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={9} tickLine={false} axisLine={false} />
                <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} unit="d" />
                <ChartTooltip
                  cursor={{ fill: '#f8fafc' }}
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px' }}
                />
                <Bar dataKey="delay" name="Avg Latency (Days)" fill="#f59e0b" radius={[4, 4, 0, 0]}>
                  {chartsData.statusDelays.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={index === 4 ? '#f43f5e' : '#f59e0b'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Productivity Comparison */}
        <div className="lg:col-span-6 bg-white border border-slate-200 p-6 rounded-xl shadow-sm flex flex-col">
          <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-6">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-500" />
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Sub-staff Performance Metrics</h3>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Total cases assigned vs successfully closed.</p>
              </div>
            </div>
          </div>
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartsData.staffProductivity} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={9} tickLine={false} axisLine={false} />
                <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} />
                <ChartTooltip
                  cursor={{ fill: '#f8fafc' }}
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px' }}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                <Bar dataKey="cases" name="Allocated Volume" fill="#2563eb" radius={[4, 4, 0, 0]} />
                <Bar dataKey="closed" name="Successfully Approved" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Downline Personnel Performance Grids */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm flex flex-col">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap items-center justify-between border-b border-slate-200 bg-slate-50/50 p-4 gap-2">
          <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200/50">
            <button
              onClick={() => setActiveTab('managers')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'managers' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Managers
            </button>
            <button
              onClick={() => setActiveTab('supervisors')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'supervisors' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Supervisors
            </button>
            <button
              onClick={() => setActiveTab('fes')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'fes' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Field Executives
            </button>
            <button
              onClick={() => setActiveTab('vendors')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'vendors' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Vendors
            </button>
            <button
              onClick={() => setActiveTab('clients')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'clients' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Clients
            </button>
          </div>
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
            Click any row to drill down cases
          </span>
        </div>

        {/* Tab content area */}
        <div className="p-2 overflow-x-auto">
          {activeTab === 'managers' && (
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-slate-500 font-bold">
                  <th className="p-4">Manager Name</th>
                  <th className="p-4">Total Cases Assigned</th>
                  <th className="p-4">Positive Verdicts (%)</th>
                  <th className="p-4">Negative Verdicts (%)</th>
                  <th className="p-4">Pending Cases</th>
                  <th className="p-4">Avg Closure TAT</th>
                  <th className="p-4 text-right">Drill-Down</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {breakdownStats.managers.map((m, idx) => (
                  <tr
                    key={idx}
                    onClick={() => handleOpenDrilldown('Manager', m.name, m.casesList)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors group"
                  >
                    <td className="p-4 font-bold text-slate-800 flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                      {m.name}
                    </td>
                    <td className="p-4 font-semibold text-slate-700">{m.total} Cases</td>
                    <td className="p-4">
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100">
                        {((m.positive / (m.total || 1)) * 100).toFixed(0)}% Clear
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Badge className="bg-red-50 text-red-700 border-red-100">
                        {((m.negative / (m.total || 1)) * 100).toFixed(0)}% Fail
                      </Badge>
                    </td>
                    <td className="p-4 font-semibold text-amber-600">{m.pending} Pending</td>
                    <td className="p-4 font-mono font-bold text-slate-800">{m.avgTat} Days</td>
                    <td className="p-4 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {activeTab === 'supervisors' && (
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-slate-500 font-bold">
                  <th className="p-4">Supervisor Name</th>
                  <th className="p-4">Assigned Leads</th>
                  <th className="p-4">Audited & Closed</th>
                  <th className="p-4">Pending QC Roster</th>
                  <th className="p-4">Revisits Raised</th>
                  <th className="p-4">QC Accuracy Score</th>
                  <th className="p-4 text-right">Drill-Down</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {breakdownStats.supervisors.map((s, idx) => (
                  <tr
                    key={idx}
                    onClick={() => handleOpenDrilldown('QC Supervisor', s.name, s.casesList)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors group"
                  >
                    <td className="p-4 font-bold text-slate-800 flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-indigo-600" />
                      {s.name}
                    </td>
                    <td className="p-4 font-semibold text-slate-700">{s.assigned} Leads</td>
                    <td className="p-4 font-semibold text-emerald-600">{s.closed} Closed</td>
                    <td className="p-4 font-semibold text-amber-600">{s.pending} Pending</td>
                    <td className="p-4 font-semibold text-rose-600">{s.revisit} Revisits</td>
                    <td className="p-4 font-bold text-slate-800">{s.score}% Match</td>
                    <td className="p-4 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {activeTab === 'fes' && (
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-slate-500 font-bold">
                  <th className="p-4">Field Executive</th>
                  <th className="p-4">Partner Role</th>
                  <th className="p-4">Assigned Visits</th>
                  <th className="p-4">Verified Clear</th>
                  <th className="p-4">Approved by QC</th>
                  <th className="p-4">Rejections / Discrepant</th>
                  <th className="p-4">Avg verification TAT</th>
                  <th className="p-4 text-right">Drill-Down</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {breakdownStats.fes.map((fe, idx) => (
                  <tr
                    key={idx}
                    onClick={() => handleOpenDrilldown('Field Executive', fe.name, fe.casesList)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors group"
                  >
                    <td className="p-4 font-bold text-slate-800 flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-600" />
                      {fe.name}
                    </td>
                    <td className="p-4 font-medium text-slate-500">{fe.role}</td>
                    <td className="p-4 font-semibold text-slate-700">{fe.assigned} Visits</td>
                    <td className="p-4 font-semibold text-emerald-600">{fe.verified} Verified</td>
                    <td className="p-4 font-semibold text-blue-600">{fe.approved} Approved</td>
                    <td className="p-4 font-semibold text-rose-600">{fe.rejected} Rejected</td>
                    <td className="p-4 font-mono font-bold text-slate-800">{fe.avgTat} Days</td>
                    <td className="p-4 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {activeTab === 'vendors' && (
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-slate-500 font-bold">
                  <th className="p-4">Vendor Partner</th>
                  <th className="p-4">Allocated Scope</th>
                  <th className="p-4">Verified Dossiers</th>
                  <th className="p-4">SLA Overdue Cases</th>
                  <th className="p-4">SLA Compliance Index</th>
                  <th className="p-4 text-right">Drill-Down</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {breakdownStats.vendors.map((v, idx) => (
                  <tr
                    key={idx}
                    onClick={() => handleOpenDrilldown('Vendor Partner', v.name, v.casesList)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors group"
                  >
                    <td className="p-4 font-bold text-slate-800 flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-purple-600" />
                      {v.name}
                    </td>
                    <td className="p-4 font-semibold text-slate-700">{v.assigned} cases</td>
                    <td className="p-4 font-semibold text-emerald-600">{v.closed} verified</td>
                    <td className="p-4 font-semibold text-rose-600">{v.overdue} Overdue</td>
                    <td className="p-4 font-bold text-slate-800">{v.score}% Quality</td>
                    <td className="p-4 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {activeTab === 'clients' && (
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-slate-500 font-bold">
                  <th className="p-4">Client Institution</th>
                  <th className="p-4">Total Cases Placed</th>
                  <th className="p-4">Successfully Completed</th>
                  <th className="p-4">Currently in Progress</th>
                  <th className="p-4">Active Products Mapped</th>
                  <th className="p-4 text-right">Drill-Down</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {breakdownStats.clients.map((c, idx) => (
                  <tr
                    key={idx}
                    onClick={() => handleOpenDrilldown('Client', c.name, c.casesList)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors group"
                  >
                    <td className="p-4 font-bold text-slate-800 flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-600" />
                      {c.name}
                    </td>
                    <td className="p-4 font-semibold text-slate-700">{c.total} Cases</td>
                    <td className="p-4 font-semibold text-emerald-600">{c.completed} Completed</td>
                    <td className="p-4 font-semibold text-amber-600">{c.pending} In Progress</td>
                    <td className="p-4 font-bold text-slate-800">{c.productsCount} Products</td>
                    <td className="p-4 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Downline Reports Downloads Matrix */}
      <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm">
        <div className="flex items-center gap-2 pb-4 border-b border-slate-100 mb-6">
          <Download className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-bold text-slate-900 text-sm">Download Performance Dossiers</h3>
            <p className="text-xs text-slate-400 font-medium mt-0.5">Generate granular performance spreadsheets and Quality logs instantly.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* FE report */}
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 flex items-center justify-between group hover:border-blue-300 transition-colors">
            <div className="space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Personnel Audit</span>
              <h4 className="text-xs font-bold text-slate-800">FE Verification Performance Log</h4>
            </div>
            <Button
              onClick={() => handleDownloadReport('Field Executive Performance spreadsheet')}
              variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-blue-50 hover:text-blue-600 bg-white"
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* Vendor report */}
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 flex items-center justify-between group hover:border-blue-300 transition-colors">
            <div className="space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Vendor Audits</span>
              <h4 className="text-xs font-bold text-slate-800">Vendor Quality Compliance SLA Report</h4>
            </div>
            <Button
              onClick={() => handleDownloadReport('Vendor Performance scorecard')}
              variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-blue-50 hover:text-blue-600 bg-white"
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* TAT report */}
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 flex items-center justify-between group hover:border-blue-300 transition-colors">
            <div className="space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">TAT Analytics</span>
              <h4 className="text-xs font-bold text-slate-800">Average Turnaround Latency Report</h4>
            </div>
            <Button
              onClick={() => handleDownloadReport('TAT Latency statistics')}
              variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-blue-50 hover:text-blue-600 bg-white"
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* QC report */}
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 flex items-center justify-between group hover:border-blue-300 transition-colors">
            <div className="space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Quality Control</span>
              <h4 className="text-xs font-bold text-slate-800">QC Rejections & Discrepancies Log</h4>
            </div>
            <Button
              onClick={() => handleDownloadReport('QC Audit exception records')}
              variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-blue-50 hover:text-blue-600 bg-white"
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* Client volume */}
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 flex items-center justify-between group hover:border-blue-300 transition-colors">
            <div className="space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Institution stats</span>
              <h4 className="text-xs font-bold text-slate-800">Client billing volume catalog</h4>
            </div>
            <Button
              onClick={() => handleDownloadReport('Client Billing ledger')}
              variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-blue-50 hover:text-blue-600 bg-white"
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* Regional report */}
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 flex items-center justify-between group hover:border-blue-300 transition-colors">
            <div className="space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Territories mapping</span>
              <h4 className="text-xs font-bold text-slate-800">Regional Territory Performance Deck</h4>
            </div>
            <Button
              onClick={() => handleDownloadReport('Regional Performance map')}
              variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-blue-50 hover:text-blue-600 bg-white"
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Drill-down slide-over Drawer Overlay */}
      {drawerOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex justify-end transition-opacity duration-300">
          {/* Main drawer body */}
          <div className="w-full max-w-3xl bg-white h-screen shadow-2xl flex flex-col animate-slide-in-right border-l border-slate-200">
            {/* Drawer Header */}
            <div className="p-6 border-b border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
              <div className="space-y-1">
                <h2 className="text-base font-extrabold text-slate-800">{drawerTitle}</h2>
                <p className="text-xs text-slate-400">Total matched cases: {drawerCases.length} records</p>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Search filter inside drawer */}
            <div className="p-4 border-b border-slate-100 shrink-0 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  value={drawerSearch}
                  onChange={(e) => setDrawerSearch(e.target.value)}
                  placeholder="Filter cases by ref, name, client..."
                  className="pl-9 bg-white border-slate-200 text-xs h-9"
                />
              </div>
            </div>

            {/* Cases Table inside drawer */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <ScrollArea className="flex-1 p-6">
                {filteredDrawerCases.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-60" />
                    <span className="text-xs font-semibold">No Matching cases found inside list.</span>
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 font-semibold text-slate-500">
                          <th className="p-3">Ref No</th>
                          <th className="p-3">Applicant</th>
                          <th className="p-3">Client</th>
                          <th className="p-3">Status</th>
                          <th className="p-3">Verdict</th>
                          <th className="p-3 text-right">Link</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredDrawerCases.map((c) => (
                          <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                            <td className="p-3 font-mono font-semibold text-blue-600 uppercase">{c.refNumber}</td>
                            <td className="p-3 font-semibold text-slate-800">{c.applicantName}</td>
                            <td className="p-3 text-slate-500">{c.client?.name || 'HDFC Bank'}</td>
                            <td className="p-3">
                              <Badge className="bg-slate-50 text-slate-700 border-slate-100 hover:bg-slate-50">
                                {c.status}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <Badge className={c.decisionStatus === 'positive' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-700 border-red-100'}>
                                {c.decisionStatus === 'positive' ? 'Positive' : 'Negative'}
                              </Badge>
                            </td>
                            <td className="p-3 text-right">
                              <Button
                                onClick={() => {
                                  setDrawerOpen(false);
                                  toast.success(`Opening documentation reports for ${c.refNumber}`);
                                  window.location.href = `/download-reports?case=${c.id}`;
                                }}
                                variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 text-xs font-bold"
                              >
                                View Dossier
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        </div>
      )}

      {/* Simulator Overlay Loading screen */}
      {downloadingReport && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full text-center border border-slate-200 space-y-4">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <div>
              <h4 className="font-semibold text-slate-800 text-sm">Compiling & Exporting Report</h4>
              <p className="text-xs text-slate-500 mt-1">Downloading {downloadingReport} as Spreadsheet format...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
